import { XIcon } from 'lucide-react';
import type { JSX } from 'react';

import type { SelectedSkill } from './useSlashSkillPicker.js';

interface SkillChipsProps {
  skills: SelectedSkill[];
  onRemove: (id: string) => void;
  /**
   * Opens the skill's card in the skills directory. A host that cannot open the directory (no
   * `ComposerPlusMenu` mounted) leaves it out, and the chip's name is then plain text.
   */
  onOpen?: (id: string) => void;
}

/**
 * Chip row for skills queued for the next send — picked either via the slash
 * popup (`SlashCommandMenu`) or the directory's "Use" action. Clicking the
 * name opens the skill; only the cross removes it, so a click meant to look
 * at a skill never drops it from the message. Hovering the name rings and
 * tints the whole pill; hovering the cross tints only the cross. Keyboard
 * focus rings the whole pill too, so the ring Radix's focus return leaves
 * behind after the directory closes follows the chip's shape.
 * Rendered next to `ComposerAttachmentTray`; `useComposer.send()` clears the
 * list once the send actually goes through (a stale-chip 422 restores it
 * instead, so the user can remove the bad chip here and resend).
 */
export function SkillChips({
  skills,
  onRemove,
  onOpen,
}: SkillChipsProps): JSX.Element | null {
  if (skills.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {skills.map((skill) => (
        <span
          key={skill.id}
          className="inline-flex max-w-[10rem] items-center gap-0.5 rounded-full bg-primary/10 py-1 pl-2 pr-1 text-sm font-medium text-primary ring-0 ring-primary/40 transition-[background-color,box-shadow] duration-150 has-[button:first-child:hover]:bg-primary/15 has-[button:first-child:hover]:ring-1 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring sm:max-w-[14rem] animate-in fade-in-0 zoom-in-95 duration-200"
        >
          {onOpen ? (
            <button
              type="button"
              onClick={() => onOpen(skill.id)}
              className="min-w-0 truncate focus-visible:outline-none"
              aria-label={`Open ${skill.title}`}
              title={`Open ${skill.title}`}
            >
              {skill.title}
            </button>
          ) : (
            <span className="min-w-0 truncate">{skill.title}</span>
          )}
          <button
            type="button"
            onClick={() => onRemove(skill.id)}
            className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none"
            aria-label={`Remove ${skill.title}`}
            title={`Remove ${skill.title}`}
          >
            <XIcon className="size-3.5" strokeWidth={2.25} aria-hidden="true" />
          </button>
        </span>
      ))}
    </div>
  );
}
